import { useMemo } from "react";

import { PricingOptionCard } from "components/pricingoptions/PricingOptionCard";
import Button from "components/Button";
import SubmitButton from "components/SubmitButton";

import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useAutoUpgradeToggleAction } from "routes/data-routes/billing-data/pricing-options/hooks/useAutoUpgradeToggleAction";
import { useAutoUpgradePrompt } from "routes/data-routes/billing-data/pricing-options/hooks/useAutoUpgradePrompt";
import { useNextHigherPlan } from "v2/hooks/billing/pricing-options/useNextHigherPlan";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";

import { cx, fmtNumber } from "utils";


export function AutoUpgradeContent(
  {
    recommended = false,
    primary = true,
    showTitle = true,
    afterSubmissionCallback
  }: {
    recommended?: boolean;
    primary?: boolean;
    showTitle?: boolean;
    afterSubmissionCallback?: () => void;
  }
) {
  const autoUpgradeToggleAction = useAutoUpgradeToggleAction();
  const { pricingOptionsFetcher, pricingOptionsError } = usePricingOptions();
  const autoUpgradePrompt = useAutoUpgradePrompt();
  const { nextHigherPlan } = useNextHigherPlan();

  useCallbackAfterSubmission(afterSubmissionCallback, pricingOptionsFetcher);

  const footerButton = useMemo(() => {
    if (pricingOptionsError) {
      if (pricingOptionsError.meta?.errorCode) {
        switch (pricingOptionsError.meta.errorCode) {
          case "err_no_payment_method":
            return <Button
              text="Add Payment Method"
              className={ cx("mt-5 w-full button", primary ? "button-primary" : "button-secondary") }
              size="MD"
              href="/billing/payment-method"
              navigationState={ { pricingOptionsWarning: false } }
            />;

          case "err_no_billing_address":
            return <Button
              text="Add Billing Address"
              className={ cx("mt-5 w-full button", primary ? "button-primary" : "button-secondary") }
              size="MD"
              href="/billing/billing-address"
              navigationState={ { pricingOptionsWarning: false } }
            />;
        }
      }

      if (pricingOptionsError.meta?.chargebee_api_error_code === "payment_processing_failed") {
        return <Button
          text="Add Payment Method"
          className={ cx("mt-5 w-full button", primary ? "button-primary" : "button-secondary") }
          size="MD"
          href="/billing/payment-method"
          navigationState={ { pricingOptionsWarning: false } }
        />;
      }

      // in case of unknown error, go to billing page
      return <Button
        text="Go to Billing Page" // TODO we should check here if we're already on the billing page or not
        className={ cx("mt-5 w-full button", primary ? "button-primary" : "button-secondary") }
        size="MD"
        href="/billing"
        navigationState={ { pricingOptionsWarning: false } }  // TODO also need to reload the pricing options data, as the fetcher only contains the error here :(
      />;
    }

    // no errors
    return <SubmitButton
      text="Enable Auto-Upgrade"
      className={ cx("mt-5 w-full button", primary ? "button-primary" : "button-secondary") }
      size="MD"
      onClick={ () => autoUpgradeToggleAction(true) }
      fetchersToCheck={ [ pricingOptionsFetcher ] }
      disabled={ pricingOptionsFetcher?.state === "loading" && !!pricingOptionsFetcher?.formData }
    />;
  }, [ pricingOptionsError, primary, autoUpgradeToggleAction, pricingOptionsFetcher ]);

  return (
    <PricingOptionCard
      recommended={ recommended }
      title={ showTitle ? <div className="text-nowrap">{ autoUpgradePrompt }{ recommended && <> (recommended)</> }</div> : undefined }
      body={ "Automatically upgrade to the next tier when your usage exceeds limits." }
      features={
        nextHigherPlan ? [
          fmtNumber(nextHigherPlan.apiCreditLimit) + " API credits / month",
          fmtNumber(nextHigherPlan.apiConcurrencyLimit) + " concurrent threads",
          nextHigherPlan.apiGeotargeting ?? "US & EU Geotargeting",
          "Prevents service interruption"
        ] : [
          "Higher credit allowance",
          "Enhanced plan features",
          "Prevents service interruption"
        ]
      }
      error={ pricingOptionsError?.message }
      footer={ footerButton }
    />
  );

}
